import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { isDemoMode } from "@/components/DemoHelpers";
import { registerDemoBlockedHandler } from "@/lib/api";

/**
 * DemoActionProvider: intercetta azioni "modificanti" (creazione preventivo,
 * acquisto, creazione immobile, upload, ecc.) quando l'utente è in modalità
 * demo, mostrando un modal informativo. In modalità demo l'azione NON viene
 * mai eseguita: la piattaforma è una vetrina read-only. L'unica CTA
 * disponibile è "Registrati" per attivare davvero il servizio.
 *
 * Uso:
 *   const promptIfDemo = useDemoAction();
 *   const submit = async () => { ...azione modificante... };
 *   const handler = () => promptIfDemo(submit, { label: "Acquisto servizio" });
 */

const DemoActionContext = createContext(() => {});

export const useDemoAction = () => useContext(DemoActionContext);

export function DemoActionProvider({ children }) {
  const [pending, setPending] = useState(null);
  // pending: { label?: string } | null (non conserviamo più onConfirm perché
  // in demo l'azione originale non viene mai eseguita)

  // Registra un handler globale invocato dall'axios interceptor quando una
  // richiesta write viene bloccata in demo mode. Deriva una label leggibile
  // dall'URL (es. "/practices/purchase" → "Acquisto pratica").
  useEffect(() => {
    const labelFromUrl = (url = "") => {
      const u = String(url).toLowerCase();
      if (u.includes("/practices/") && u.includes("/deliver")) return "Consegna lavoro";
      if (u.includes("/practices/") && u.includes("/revoke")) return "Annullamento pratica";
      if (u.includes("/practices/") && u.includes("/reassign")) return "Riassegnazione operatore";
      if (u.includes("/practices/") && u.includes("/documents")) return "Upload documento";
      if (u.includes("/practices/") && u.includes("/forms")) return "Compilazione form";
      if (u.includes("/practices/purchase") || u.includes("/practices/") && u.endsWith("/practices")) return "Prenotazione servizio";
      if (u.includes("/quotes")) return "Generazione preventivo";
      if (u.includes("/properties")) return "Creazione/modifica immobile";
      if (u.includes("/services")) return "Creazione/modifica servizio";
      if (u.includes("/webauthn")) return "Attivazione passkey";
      if (u.includes("/2fa")) return "Configurazione 2FA";
      return "Azione modificante";
    };
    registerDemoBlockedHandler(({ url }) => setPending({ label: labelFromUrl(url) }));
    return () => registerDemoBlockedHandler(null);
  }, []);

  const runOrPrompt = useCallback((onConfirm, opts = {}) => {
    if (!isDemoMode()) {
      onConfirm();
      return;
    }
    // In demo: mostra il prompt e NON eseguire l'azione (read-only).
    setPending({ ...opts });
  }, []);

  const close = () => setPending(null);

  return (
    <DemoActionContext.Provider value={runOrPrompt}>
      {children}
      <Dialog open={!!pending} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md" data-testid="demo-action-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">🎬</span> Sei in modalità demo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <p>
              Stai esplorando Housetag come vetrina interattiva.
              I servizi, gli immobili e la disponibilità dei nostri operatori
              che vedi sono <strong>reali</strong>, ma per prenotarli davvero
              e avviare le pratiche è necessario un account.
            </p>
            {pending?.label && (
              <p className="text-xs text-muted-foreground border-l-2 border-purple-300 pl-3">
                Azione richiesta: <span className="font-mono text-purple-800">{pending.label}</span>
              </p>
            )}
            <p className="text-sm">
              <strong>Registrati gratuitamente in un minuto</strong> per attivare i servizi,
              gestire i tuoi immobili e i tuoi clienti.
            </p>
          </div>
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-3 border-t mt-3">
            <Button
              type="button"
              variant="ghost"
              onClick={close}
              className="flex-1 sm:flex-none"
              data-testid="demo-action-close"
            >
              Continua a esplorare
            </Button>
            <Link
              to="/register"
              onClick={close}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md ht-gradient border-0 text-white font-semibold text-sm flex-1 sm:flex-none"
              data-testid="demo-action-register"
            >
              Registrati per prenotare →
            </Link>
          </div>
        </DialogContent>
      </Dialog>
    </DemoActionContext.Provider>
  );
}
